"use client";

import { useEffect, useRef, useState } from "react";

interface ScrollRangeSelectorProps {
  value?: number;
  start?: number;
  end?: number;
  step?: number;
  width?: number;
  onChange?: (value: number) => void;
}

export default function ScrollRangeSelector({
  value = 11,
  start = 1,
  end = 31,
  step = 1,
  width = 350,
  onChange,
}: ScrollRangeSelectorProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(value);
  const [showError, setShowError] = useState(false);
  const itemWidth = 21;
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const isManualInput = useRef(false);

  const numbers = Array.from(
    { length: Math.floor((end - start) / step) + 1 },
    (_, i) => start + i * step,
  );

  // --- Scroll logic ---
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const handleScroll = () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      if (isManualInput.current) return;

      scrollTimeout.current = setTimeout(() => {
        const scrollLeft = scroller.scrollLeft;
        const centerIndex = Math.round(scrollLeft / itemWidth);
        const centerValue = start + centerIndex * step;

        if (centerValue >= start && centerValue <= end) {
          setSelected(centerValue);
          setShowError(false);
          onChange?.(centerValue);
        }

        // Snap to nearest
        const targetScrollLeft = centerIndex * itemWidth;
        scroller.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
      }, 120);
    };

    const handleWheel = (event: WheelEvent) => {
      if (!scroller) return;
      event.preventDefault();
      const scrollSpeed = 0.3;
      scroller.scrollLeft += event.deltaY * scrollSpeed;
    };

    scroller.addEventListener("scroll", handleScroll, { passive: true });
    scroller.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      scroller.removeEventListener("scroll", handleScroll);
      scroller.removeEventListener("wheel", handleWheel);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [start, end, step, onChange]);

  // --- Sync input & scroll ---
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // const index = Math.round((selected - start) / step);
    // const targetScrollLeft = index * itemWidth;

    // scroller.scrollTo({ left: targetScrollLeft, behavior: "smooth" });

    if (selected >= start && selected <= end) {
      const index = Math.round((selected - start) / step);
      const targetScrollLeft = index * itemWidth;
      scroller.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
    }
  }, [selected, start, step]);

  // --- Handle input typing ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);

    if (isNaN(value)) return;

    if (value < start) {
      setShowError(true);
      setSelected(0);
      onChange?.(0);
      return;
    }

    if (value > end) {
      setSelected(end);
      return;
    }

    setShowError(false);
    isManualInput.current = true;

    setSelected(value);
    onChange?.(value);

    const scroller = scrollerRef.current;
    if (scroller) {
      const index = Math.round((value - start) / step);
      const targetScrollLeft = index * itemWidth;
      scroller.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
    }

    setTimeout(() => {
      isManualInput.current = false;
    }, 400);
  };

  const nullCount = 8;
  const extendedNumbers = numbers.concat(Array(nullCount).fill(null));

  return (
    <>
      {/* Input Field */}
      <label
        className={`relative block w-full max-w-28 mx-auto bg-transparent py-1 px-2 rounded-lg border border-[#FFFFFF45] mb-5 ${showError ? "border-[#ec4d4d]" : "border-[#FFFFFF45]"}`}
      >
        <span
          className={`absolute right-4 top-1/2 -translate-y-1/2 font-semibold pointer-events-none ${showError ? "text-[#ec4d4d] " : "text-[#FFFFFF80] "}`}
        >
          Years
        </span>
        <input
          type="text"
          value={selected}
          onChange={handleInputChange}
          className={`px-2 border-y border-dotted bg-transparent border-t-transparent font-semibold w-full transition-all duration-200 ease-in-out text-xl ${showError ? "border-[#ec4d4d] text-[#ec4d4d] " : "border-b-[#e4e4e4] hover:border-b-[#b6b5b5] text-white "}`}
        />
      </label>

      {/* Number Picker */}
      <div className="relative flex flex-col items-center mb-5">
        <div
          className="overflow-x-auto h-16 overflow-y-hidden scrollbar-none select-none"
          ref={scrollerRef}
          style={{ width: `${width}px`, scrollSnapType: "none" }}
        >
          <div
            className="flex items-center"
            style={{ paddingLeft: `${width / 2 - itemWidth / 2}px` }}
          >
            {extendedNumbers.map((num, index) => {
              const isNotNull = num !== null;
              const isActive = num <= selected;
              const breakPoints = index % 5 === 0;

              return (
                <div
                  key={index}
                  className="relative shrink-0 flex flex-col items-center text-center transition-all duration-200"
                  style={{ width: `${itemWidth}px` }}
                >
                  <i
                    className={`inline-block w-0.5 ${isNotNull ? (isActive ? "bg-white" : "bg-[#858585]") : "bg-transparent"} ${breakPoints ? "h-9" : "h-6"}`}
                  />
                  <span
                    className={`absolute -bottom-7 inset-x-0 mx-auto text-sm font-semibold ${isActive ? "text-white" : "text-[#858585]"}`}
                  >
                    {breakPoints && num !== null && num <= end ? num : ""}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Center Indicator */}
          <span className="inline-block absolute left-0 right-0 mx-auto w-0.75 top-0 h-9 bg-white pointer-events-none" />
        </div>
      </div>
    </>
  );
}
